'use client';

import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SellerStats } from '@kit/marketplace/services/review';
import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

/**
 * Compact, clickable seller rating: `★ 4.9 (23)`, or "No reviews yet" when the
 * seller has none. Opens the reviews drawer via `onClick`. Presentational —
 * the launcher owns the drawer state.
 */
interface RatingBadgeProps {
  stats: SellerStats;
  onClick: () => void;
  size?: number;
  className?: string;
}

export function RatingBadge({
  stats,
  onClick,
  size = 14,
  className,
}: RatingBadgeProps) {
  const { t } = useTranslation();
  if (!stats.reviewCount) {
    return (
      <Button
        type="button"
        variant="link"
        onClick={onClick}
        className={cn(
          'text-muted-foreground hover:text-foreground h-auto p-0 text-xs transition-colors',
          className,
        )}
      >
        {t('explore:ratingBadge.noReviews')}
      </Button>
    );
  }
  return (
    <Button
      type="button"
      variant="link"
      onClick={onClick}
      className={cn(
        'text-primary inline-flex h-auto items-center gap-1 p-0 text-sm hover:underline',
        className,
      )}
      aria-label={t('explore:ratingBadge.ariaLabel', {
        rating: stats.avgRating.toFixed(1),
        count: stats.reviewCount,
      })}
    >
      <Star width={size} height={size} fill="currentColor" />
      <span className="text-foreground font-semibold">
        {stats.avgRating.toFixed(1)}
      </span>
      <span className="text-muted-foreground">({stats.reviewCount})</span>
    </Button>
  );
}
